import { masterpassTokenReqBodyData } from 'Config';
import { MasterPassTokenEnum } from 'Utils';

/**
 * `RI.OnGenerateTLVToken` istek gövdesini kurar.
 *
 * ─── TEMEL KURAL ──────────────────────────────────────────────────────────────
 * ⚠️ GÖVDE, v2 GEÇİŞİNDEN ÖNCEKİ HÂLİYLE BİREBİR AYNIDIR. v1/v2 ayrımı YALNIZCA
 * CEVAPTA yapılır (`IsVersion2Active`); istekte sürüme göre hiçbir dallanma yoktur.
 *
 * Servis token'ı İŞLEM BAŞINA üretir, bu yüzden gövde işlemin bağlamını taşımak
 * zorundadır: hangi işlem (`TokenType`), hangi yükleme tipi (`RefTrnType`), ne
 * kadar (`Amount`), hangi kart (`MifareId` / `PmtAccountId` / `PanMasked`)...
 * Eski saga bu yüzden payload'ın TAMAMINI gönderiyordu (`requestQuery = payload`).
 *
 * ─── BU DOSYA NEDEN VAR ───────────────────────────────────────────────────────
 * Bir dönem uygulama `RI.OnGenerateJwtoken` adlı ayrı bir servise bağlanmıştı.
 * O servis OTURUM seviyesinde tek bir JWT üretiyordu ve işlem bağlamı görünce
 * hata veriyordu; bu dosya da o sözleşmeye göre gövdeyi sadeleştiriyordu:
 *
 *   ödeme dışı → { CustomerNumber, SessionToken }            (TokenType YOK)
 *   kart kaydı → { CustomerNumber, SessionToken, TokenType: 2 }
 *   ödeme      → { CustomerNumber, SessionToken, TokenType: 1, ... }
 *
 * ⚠️ O KURALLAR ARTIK GEÇERSİZ. JWT servisi kaldırıldı; TLV servisi bağlam
 * gelmeyince `99 GlobalException` döner:
 *
 *   İstek : { CustomerNumber: 1157406, SessionToken: "51b1…" }
 *   Cevap : ResponseCode 99 — "GlobalException"
 *
 * ⚠️ `TokenType` SABİTLENMEZ. JWT servisinde ödeme çağrıları sabit `1`, kart
 * kaydı sabit `2` ile gidiyordu. TLV servisi ise `MasterPassTokenEnum`'un GERÇEK
 * değerini bekler (2 = kart kaydı, 3 = kart listesi, 4 = kayıt+ödeme, 6 = kart
 * silme, …). Enum değerlerinin v2 geçişinde kaymadığı iki sürüm karşılaştırılarak
 * doğrulandı.
 *
 * ─── SERVİSE GİDEN "FAZLALIK" ALANLAR ─────────────────────────────────────────
 * Gövdede frontend'e özgü `request` alanı da bulunur (içinde SDK çağrısının kendi
 * gövdesi vardır). Bu, v2 öncesinde de böyleydi ve servis tolere ediyordu; bilerek
 * ELENMİYOR — eski davranıştan sapmamak, gövdeyi "temizlemekten" önemlidir.
 *
 * Yalnızca `forceRefresh` elenir: o, kart listesi önbelleğini atlatmak için sonradan
 * eklenmiş bir frontend bayrağıdır ve servis onu hiç görmedi.
 */

/**
 * JWT'nin ödeme bağlamıyla üretilmesi gereken işlemler.
 *
 * ⚠️ Bu liste artık İSTEK GÖVDESİNİ etkilemez — gövde her işlem tipinde aynı
 * kurala göre kurulur. Yalnızca token'ın ÖNBELLEK KAPSAMINI belirlemek için
 * kullanılır: ödeme için alınmış bir token hesap işlemlerinde kullanılırsa
 * Masterpass `403 AUTHENTICATION_FAILED` döndürüyor (v2'de canlıda gözlendi).
 */
const PAYMENT_TOKEN_TYPES: MasterPassTokenEnum[] = [
  MasterPassTokenEnum.REGISTER_PURCHASE,
  MasterPassTokenEnum.RECURING_PAYMENT,
  MasterPassTokenEnum.INITIATE_RECURRING_PAYMENT,
];

/**
 * Web istemcisi Masterpass v2 akışını destekliyor mu?
 *
 * ⚠️ SABİT `true` — ve öyle kalmalı. Sürüm kararı servistedir ve bir VE kapısıdır:
 *   IsVersion2Active (yanıt) = IsVersion2Supported (istek) && IsMasterpassV2Active (config)
 *
 * Alanın var olma sebebi MOBİL: orada eski uygulama sürümleri zorla
 * güncellenemediği için v2'yi desteklemeyen istemciler sahada kalabiliyor ve
 * `false` göndererek v1'de kalmaları gerekiyor. Web'de bu sorun yok — kullanıcı
 * her yüklemede en güncel bundle'ı alır ve iki entegrasyon da uygulamada mevcut.
 *
 * Bu değeri `false` yapmak v2'yi TAMAMEN kapatır (config açık olsa bile).
 * Uygulamayı v1'e sabitlemek gerekirse doğru kaldıraç budur.
 */
const WEB_SUPPORTS_MASTERPASS_V2 = true;

export const isMasterpassPaymentTokenType = (tokenType?: MasterPassTokenEnum): boolean =>
  !!tokenType && PAYMENT_TOKEN_TYPES.includes(tokenType);

/**
 * @param payload Saga'ya gelen ham Redux payload'ı (PanMasked saga'da maskelenmiş hâlde gelir).
 * @param isDigitalActive Dijital hesap yükleme akışı aktif mi.
 * @param isCardApplicationProcess Kart başvurusu akışı aktif mi.
 */
export const masterpassTokenNormalize = (
  payload: any,
  isDigitalActive?: boolean,
  isCardApplicationProcess?: boolean
): masterpassTokenReqBodyData => {
  // Eski davranış: payload olduğu gibi gider. Yalnızca sonradan eklenen frontend
  // bayrağı ayıklanır.
  const { forceRefresh, ...requestQuery } = payload ?? {};

  // Dijital hesaba yükleme yapılıyorsa fiziksel kart alanları anlamsızdır ve işlem
  // tipi dijital bakiyeye çevrilir.
  //
  // ⚠️ Kart başvurusu akışında bu override UYGULANMAZ: `isDigitalActive` önceki bir
  // dijital hesap ziyaretinden kalmış (sticky) olabiliyor ve başvurunun kendi işlem
  // tipini (yeni kart = 1, ikinci kargo = 11) yanlışlıkla 6'ya eziyor.
  if (isDigitalActive && !isCardApplicationProcess) {
    delete requestQuery.PmtAccountId;
    delete requestQuery.MifareId;
    requestQuery.RefTrnType = 6;
  }

  // ⚠️ HER İSTEKTE gönderilir — yalnızca ödeme ya da yalnızca hesap işlemlerinde
  // değil. Servis sürüm kararını her token üretiminde yeniden veriyor; alan
  // eksik kaldığı çağrı v1'e düşer ve akış ortasında sürüm değişir.
  return { ...requestQuery, IsVersion2Supported: WEB_SUPPORTS_MASTERPASS_V2 };
};
